import { identity } from "lodash";
import { AppState } from "./timesheet";
import { TimesheetEntry } from "./types";
import uid from "short-uuid";

function loadValue<T>(
  key: string,
  defaultValue: T,
  deserializer: (val: any) => T = identity
) {
  const item = localStorage.getItem(key);
  try {
    const value =
      typeof item !== "string"
        ? defaultValue
        : (deserializer(JSON.parse(item)) as T);

    return value;
  } catch (err) {
    // tslint:disable-next-line: no-console
    console.warn("found a corrupted localStorage value: '%s'", item);
    return defaultValue;
  }
}

function loadState(sheetName?: string | null): AppState {
  const sheets = StorageApi.loadValue(
    "pac.timesheet.sheets",
    sheetName ? [sheetName] : ["Unbenannt"]
  );
  const storageKey = `pac.timesheet.entries.${sheets[0]}`;
  const entries = StorageApi.loadValue<TimesheetEntry[]>(
    storageKey,
    [],
    deserializeEntries
  );

  return { sheets, entries, latestImport: [] };
}

function saveState(state: AppState) {
  localStorage.setItem("pac.timesheet.sheets", JSON.stringify(state.sheets));

  localStorage.setItem(
    `pac.timesheet.entries.${state.sheets[0]}`,
    JSON.stringify(state.entries)
  );
}

function deserializeEntries(
  entries: Array<TimesheetEntry & { start: string }>
) {
  return entries.map(({ start, id, ...rest }) => ({
    ...rest,
    // migration for older versions without id
    id: id ? id : uid.generate(),
    start: new Date(start)
  }));
}

function startSync(
  activeSheet: string,
  onSync: (patch: Partial<AppState>) => void
) {
  function sync(ev: StorageEvent) {
    if (ev.oldValue === ev.newValue) {
      return;
    }
    switch (ev.key) {
      case `pac.timesheet.entries.${activeSheet}`:
        return onSync({
          entries: StorageApi.loadValue(
            `pac.timesheet.entries.${activeSheet}`,
            [],
            deserializeEntries
          )
        });
      case "pac.timesheet.sheets":
        return onSync({
          sheets: StorageApi.loadValue(ev.key, [])
        });
    }
  }

  window.addEventListener("storage", sync);

  return () => window.removeEventListener("storage", sync);
}

function removeEntries(sheetName: string) {
  localStorage.removeItem(`pac.timesheet.entries.${sheetName}`);
}

function loadEntries(sheetName: string) {
  return loadValue(
    `pac.timesheet.entries.${sheetName}`,
    [],
    deserializeEntries
  );
}

const StorageApi = {
  loadValue,
  saveState,
  loadState,
  sync: startSync,
  removeEntries,
  loadEntries
};

export default StorageApi;
